/**
 * «Questa settimana» — cosa stiamo facendo adesso, scritto a mano.
 *
 * Sostituisce la casella «In corso», che era **derivata** dagli stati dei
 * moduli: rispondeva «M3 è in beta» a chi chiedeva «cosa state facendo».
 *
 * ## Perché mostra quanto è vecchia
 *
 * La Panoramica non è morta per come era disegnata, è morta perché nessuno
 * l'ha più aggiornata. Un blocco scritto a mano è una promessa di manutenzione,
 * e un «questa settimana» fermo a tre settimane fa è peggio del blocco derivato
 * che sostituisce — perché quello almeno non mentiva mai.
 *
 * Quindi: la data dell'ultima modifica è sempre in chiaro, e oltre
 * `GIORNI_STANTIO` il blocco si spegne (grigio + avviso) invece di travestirsi
 * da notizia fresca. La trascuratezza si deve vedere.
 */

import { AlertTriangle, ListChecks } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { useSettimana } from "@/hooks/useSettimana";
import { cn } from "@/lib/utils";

/** Oltre questa età il blocco è dichiarato vecchio. Due settimane di grazia. */
const GIORNI_STANTIO = 14;

function giorniDa(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function etaLabel(giorni: number): string {
  if (giorni <= 0) return "aggiornato oggi";
  if (giorni === 1) return "aggiornato ieri";
  return `aggiornato ${giorni} giorni fa`;
}

export function QuestaSettimana() {
  const { data, isLoading } = useSettimana();

  const voci = data?.voci ?? [];
  const giorni = data?.aggiornato_il ? giorniDa(data.aggiornato_il) : null;
  const stantio = giorni !== null && giorni > GIORNI_STANTIO;

  return (
    <div className="rounded-lg border bg-card h-full p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
          <ListChecks className="size-3.5" />
          Questa settimana
        </span>
        {voci.length > 0 && (
          <span className="text-xs text-muted-foreground font-mono">{voci.length}</span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
      ) : voci.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nessun lavoro in corso dichiarato.
        </p>
      ) : (
        <ul className={cn("space-y-3", stantio && "opacity-50")}>
          {voci.map((v) => (
            <li key={v.id} className="space-y-0.5">
              <div className="flex gap-2 items-baseline">
                <span
                  className="mt-1.5 size-1.5 rounded-full bg-primary shrink-0"
                  aria-hidden
                />
                <p className="text-sm font-medium leading-snug">{v.titolo}</p>
              </div>
              {v.dettaglio && (
                <p className="text-xs text-muted-foreground leading-relaxed pl-3.5">
                  {v.dettaglio}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {giorni !== null &&
        (stantio ? (
          <p className="text-xs text-warning inline-flex items-center gap-1.5 pt-1 border-t w-full">
            <AlertTriangle className="size-3 shrink-0" />
            {etaLabel(giorni)} — potrebbe non essere più attuale
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground pt-1 border-t">
            {etaLabel(giorni)}
          </p>
        ))}
    </div>
  );
}
